import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/bits";
import { FileUpload } from "../components/FileUpload";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function PlatformSettings() {
  const [form, setForm] = useState(null);
  const [gc, setGc] = useState({ client_id: "", client_secret: "", configured: false, has_secret: false, redirect_uri: "" });
  const [pres, setPres] = useState({ pres_badge: "", pres_headline: "", pres_subheadline: "", pres_cta: "", trial_days: 15 });

  useEffect(() => {
    api.get("/platform/settings").then((r) => {
      setForm({ ...r.data, phones: (r.data.phones || []).join(", "), emails: (r.data.emails || []).join(", ") });
      setPres({
        pres_badge: r.data.pres_badge || "", pres_headline: r.data.pres_headline || "",
        pres_subheadline: r.data.pres_subheadline || "", pres_cta: r.data.pres_cta || "",
        trial_days: r.data.trial_days || 15,
      });
    }).catch((e) => toast.error(formatApiError(e)));
    api.get("/integrations/google-classroom/config").then((r) => setGc({ client_id: r.data.client_id || "", client_secret: "", configured: r.data.configured, has_secret: r.data.has_secret, redirect_uri: r.data.redirect_uri || "" })).catch(() => {});
  }, []);

  if (!form) return <p className="text-slate-400 text-sm">Cargando datos de empresa…</p>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const isUploaded = (v) => v?.startsWith("data:") || v?.startsWith("/api/files/");

  const UploadButton = ({ field, testid }) => (
    <FileUpload category="branding" accept="image/*" testid={testid} label="Subir"
      onUploaded={(f) => setForm((prev) => ({ ...prev, [field]: f.url }))} />
  );

  const gcSave = async () => {
    try {
      const r = await api.put("/integrations/google-classroom/config", { client_id: gc.client_id, client_secret: gc.client_secret });
      setGc({ ...gc, configured: true, has_secret: true, client_secret: "", redirect_uri: r.data.redirect_uri });
      toast.success("Credenciales de Google Classroom guardadas — los maestros ya pueden conectar su cuenta");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const presSave = async () => {
    try {
      await api.put("/platform/settings", {
        pres_badge: pres.pres_badge, pres_headline: pres.pres_headline,
        pres_subheadline: pres.pres_subheadline, pres_cta: pres.pres_cta,
        trial_days: parseInt(pres.trial_days) || 15,
      });
      toast.success("Página de presentación actualizada — visible en el link público");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async () => {
    try {
      await api.put("/platform/settings", {
        ...form,
        phones: form.phones.split(",").map((s) => s.trim()).filter(Boolean),
        emails: form.emails.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Datos de empresa guardados — se aplican en toda la plataforma");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="platform-settings-page">
      <PageHeader title="Mi Empresa" subtitle="Datos que aparecen en la facturación a colegios, el login, el menú y el ícono del navegador" />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
          <h3 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2">Datos fiscales y de contacto</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Nombre de la empresa</Label><Input data-testid="company-name-input" value={form.company_name} onChange={set("company_name")} /></div>
            <div className="space-y-1.5"><Label>RNC</Label><Input data-testid="company-rnc-input" value={form.rnc} onChange={set("rnc")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Dirección</Label><Input data-testid="company-address-input" value={form.address} onChange={set("address")} /></div>
          <div className="space-y-1.5"><Label>Teléfonos (separados por coma)</Label><Input data-testid="company-phones-input" value={form.phones} onChange={set("phones")} /></div>
          <div className="space-y-1.5"><Label>Correos (separados por coma)</Label><Input data-testid="company-emails-input" value={form.emails} onChange={set("emails")} /></div>

          <h3 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-4">Textos del login</h3>
          <div className="space-y-1.5"><Label>Eslogan bajo el nombre (tagline)</Label><Input data-testid="company-tagline-input" value={form.tagline || ""} onChange={set("tagline")} placeholder="Todo tu colegio. Una sola plataforma." /></div>
          <div className="space-y-1.5"><Label>Frase de la portada (lema)</Label><Input data-testid="company-slogan-input" value={form.slogan || ""} onChange={set("slogan")} placeholder="La educación nos une" /></div>

          <h3 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-4">Identidad visual (aplica a toda la plataforma y sus colegios)</h3>
          <div className="space-y-1.5">
            <Label>URL del logo</Label>
            <div className="flex gap-2">
              <Input data-testid="company-logo-input" placeholder="https://… o sube un archivo" value={isUploaded(form.logo_url) ? "(archivo subido)" : form.logo_url} onChange={set("logo_url")} />
              <UploadButton field="logo_url" testid="upload-logo-file" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Tamaño del logo (alto en px, 24–120)</Label>
            <div className="flex items-center gap-3">
              <input
                type="range" min="24" max="120" step="2"
                data-testid="company-logo-size-range"
                className="flex-1 accent-blue-800"
                value={form.logo_size || 40}
                onChange={(e) => setForm({ ...form, logo_size: parseInt(e.target.value) })}
              />
              <Input
                type="number" min="24" max="120" className="w-20"
                data-testid="company-logo-size-input"
                value={form.logo_size || 40}
                onChange={(e) => setForm({ ...form, logo_size: Math.min(120, Math.max(24, parseInt(e.target.value) || 40)) })}
              />
              <span className="text-xs text-slate-500 w-10">px</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              data-testid="company-show-name-switch"
              checked={form.show_name !== false}
              onCheckedChange={(v) => setForm({ ...form, show_name: v })}
            />
            <Label className="!mt-0">Mostrar el nombre de la empresa junto al logo en las facturas</Label>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              data-testid="company-logo-has-name-switch"
              checked={form.logo_has_name === true}
              onCheckedChange={(v) => setForm({ ...form, logo_has_name: v })}
            />
            <Label className="!mt-0">Mi logo ya incluye el nombre y eslogan (en el login se mostrará solo el logo)</Label>
          </div>
          <div className="space-y-1.5">
            <Label>URL de foto de portada (pantalla de login)</Label>
            <div className="flex gap-2">
              <Input data-testid="company-cover-input" placeholder="https://… o sube un archivo" value={isUploaded(form.cover_url) ? "(archivo subido)" : form.cover_url} onChange={set("cover_url")} />
              <UploadButton field="cover_url" testid="upload-cover-file" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              data-testid="company-cover-has-design"
              checked={form.cover_has_design === true}
              onCheckedChange={(v) => setForm({ ...form, cover_has_design: v })}
            />
            <Label className="!mt-0">Mi portada ya incluye el diseño (logo, características y lema) — mostrar solo la imagen</Label>
          </div>
          <div className="space-y-1.5">
            <Label>Encuadre de la portada</Label>
            <Select value={form.cover_position || "center"} onValueChange={(v) => setForm({ ...form, cover_position: v })}>
              <SelectTrigger data-testid="company-cover-position"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="top">Arriba</SelectItem>
                <SelectItem value="center">Centro</SelectItem>
                <SelectItem value="bottom">Abajo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Tamaño de la portada (zoom)</Label>
            <div className="flex items-center gap-3">
              <input
                type="range" min="100" max="200" step="5"
                data-testid="company-cover-zoom-range"
                className="flex-1 accent-blue-800"
                value={form.cover_zoom ?? 100}
                onChange={(e) => setForm({ ...form, cover_zoom: parseInt(e.target.value) })}
              />
              <Input
                type="number" min="100" max="200" step="5" className="w-20"
                data-testid="company-cover-zoom-input"
                value={form.cover_zoom ?? 100}
                onChange={(e) => setForm({ ...form, cover_zoom: Math.min(200, Math.max(100, parseInt(e.target.value) || 100)) })}
              />
              <span className="text-xs text-slate-500 w-8">%</span>
            </div>
            <p className="text-xs text-slate-400">La imagen siempre cubre todo el espacio en cualquier dispositivo sin deformarse. El zoom amplía desde el encuadre elegido (100% = exacta).</p>
          </div>
          <div className="space-y-1.5">
            <Label>URL del ícono (favicon y menú lateral)</Label>
            <div className="flex gap-2">
              <Input data-testid="company-icon-input" placeholder="https://… o sube un archivo" value={isUploaded(form.icon_url) ? "(archivo subido)" : form.icon_url} onChange={set("icon_url")} />
              <UploadButton field="icon_url" testid="upload-icon-file" />
            </div>
          </div>

          <h3 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-4">Integración Google Classroom</h3>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3" data-testid="gc-config-section">
            <p className="text-xs text-slate-500">Credenciales OAuth de Google Cloud Console (proyecto con la API de Classroom habilitada). En Google Cloud agrega este URI de redirección autorizado:</p>
            <div className="flex gap-2 items-center">
              <Input readOnly value={gc.redirect_uri} data-testid="gc-redirect-uri" className="font-mono text-xs bg-white" />
              <Button type="button" variant="outline" size="sm" data-testid="gc-copy-redirect" onClick={() => { navigator.clipboard.writeText(gc.redirect_uri); toast.success("URI copiado"); }}>Copiar</Button>
            </div>
            <div className="space-y-1.5">
              <Label>Client ID</Label>
              <Input data-testid="gc-client-id" value={gc.client_id} onChange={(e) => setGc({ ...gc, client_id: e.target.value })} placeholder="xxxx.apps.googleusercontent.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Client Secret {gc.has_secret && <span className="text-xs text-emerald-600 font-normal">(guardado cifrado — deja vacío para mantenerlo)</span>}</Label>
              <Input type="password" data-testid="gc-client-secret" value={gc.client_secret} onChange={(e) => setGc({ ...gc, client_secret: e.target.value })} placeholder="GOCSPX-…" />
            </div>
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" size="sm" data-testid="gc-save" onClick={gcSave}>Guardar credenciales</Button>
              {gc.configured && <span className="text-xs font-medium text-emerald-700" data-testid="gc-configured-badge">Configurado — los maestros ya pueden conectar</span>}
            </div>
          </div>

          <h3 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-4">Página de presentación (link público)</h3>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3" data-testid="pres-config-section">
            <p className="text-xs text-slate-500">Este es el link público con la propuesta de la plataforma y el registro de demo. Compártelo con colegios interesados:</p>
            <div className="flex gap-2 items-center">
              <Input readOnly value={`${window.location.origin}/presentacion`} className="font-mono text-xs bg-white" data-testid="pres-public-link" />
              <Button type="button" variant="outline" size="sm" data-testid="pres-copy-link" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/presentacion`); toast.success("Link copiado"); }}>Copiar</Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Badge del encabezado (vacío = oculto)</Label>
                <Input data-testid="pres-badge" value={pres.pres_badge} onChange={(e) => setPres({ ...pres, pres_badge: e.target.value })} placeholder="Ej: Plataforma de gestión escolar con IA" />
              </div>
              <div className="space-y-1.5">
                <Label>Días de prueba gratis</Label>
                <Input type="number" min="1" data-testid="pres-trial-days" value={pres.trial_days} onChange={(e) => setPres({ ...pres, trial_days: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Título principal (hero)</Label>
              <Input data-testid="pres-headline" value={pres.pres_headline} onChange={(e) => setPres({ ...pres, pres_headline: e.target.value })} placeholder="El ecosistema digital que conecta a tu colegio…" />
            </div>
            <div className="space-y-1.5">
              <Label>Subtítulo</Label>
              <Input data-testid="pres-subheadline" value={pres.pres_subheadline} onChange={(e) => setPres({ ...pres, pres_subheadline: e.target.value })} placeholder="Texto debajo del título (vacío = texto por defecto)" />
            </div>
            <div className="space-y-1.5">
              <Label>Texto del botón principal</Label>
              <Input data-testid="pres-cta" value={pres.pres_cta} onChange={(e) => setPres({ ...pres, pres_cta: e.target.value })} placeholder={`Empezar prueba de ${pres.trial_days} días`} />
            </div>
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" size="sm" data-testid="pres-save" onClick={presSave}>Guardar presentación</Button>
              <a href="/presentacion" target="_blank" rel="noreferrer" className="text-xs text-sky-700 hover:underline" data-testid="pres-preview-link">Ver la página pública ↗</a>
            </div>
          </div>

          <div className="pt-2">
            <Button data-testid="save-platform-settings" className="bg-blue-800 hover:bg-blue-700" onClick={save}>
              <Save className="w-4 h-4 mr-1" /> Guardar
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Vista previa — logo ({form.logo_size || 40}px)</p>
            {form.logo_url ? (
              <img src={form.logo_url} alt="logo" className="w-auto object-contain" style={{ height: form.logo_size || 40 }} data-testid="logo-preview" />
            ) : <p className="text-xs text-slate-400">Sin logo configurado</p>}
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Vista previa — portada ({form.cover_zoom ?? 100}%)</p>
            {form.cover_url ? (
              <div className="w-full h-32 rounded-lg overflow-hidden bg-slate-100">
                <img
                  src={form.cover_url}
                  alt="portada"
                  className="w-full h-full object-cover transition-transform"
                  style={{ objectPosition: form.cover_position || "center", transform: `scale(${(form.cover_zoom ?? 100) / 100})` }}
                  data-testid="cover-preview"
                />
              </div>
            ) : <p className="text-xs text-slate-400">Sin portada configurada</p>}
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Vista previa — ícono</p>
            {form.icon_url ? (
              <img src={form.icon_url} alt="icon" className="h-10 w-10 object-contain rounded" data-testid="icon-preview" />
            ) : <p className="text-xs text-slate-400">Sin ícono configurado</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
